import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Col,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import { Link } from "react-router-dom";
import classnames from "classnames";
import { toast } from "react-toastify";

import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";

import bell from "../../assets/images/svg/bell.svg";

import dummyImg from "../../assets/images/users/user-dummy-img.jpg";
import companyImg from "../../assets/images/users/logo-sm.png";

import {
  getNotifications as onGetNotifications,
  updateNotification as onGetUpdateNotification,
  getUsers as onGetUsers,
} from "../../slices/thunks";

//SimpleBar
import SimpleBar from "simplebar-react";

const NotificationDropdown = () => {
  const dispatch = useDispatch();

  const authUser = JSON.parse(localStorage.getItem("authUser"));

  const [notificationsList, setNotificationsList] = useState([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [prevUnreadCount, setPrevUnreadCount] = useState(0);
  const [soundAllowed, setSoundAllowed] = useState(false);

  const prevUnreadCountRef = useRef(0);

  const [isNotificationDropdown, setIsNotificationDropdown] = useState(false);
  const toggleNotificationDropdown = () => {
    setIsNotificationDropdown(!isNotificationDropdown);
  };

  const selectNotificationLayoutState = (state) => state.Notification;
  const notificationData = createSelector(
    selectNotificationLayoutState,
    (state) => ({
      notifications: state.notifications,
      isNotificationsSuccess: state.isNotificationsSuccess,
      error: state.error,
    })
  );

  const { notifications, isNotificationsSuccess, error } =
    useSelector(notificationData);

  const selectLayoutState = (state) => state.Crm;
  const crmuserData = createSelector(selectLayoutState, (state) => ({
    crmusers: state.crmusers,
    isUserSuccess: state.isUserSuccess,
    error: state.error,
  }));

  const { crmusers, isUserSuccess } = useSelector(crmuserData);

  useEffect(() => {
    if (crmusers && !crmusers.length) {
      dispatch(onGetUsers());
    }
  }, [dispatch, crmusers]);

  useEffect(() => {
    const getTunnel = () => {
      if (authUser?.company === "TILogistic") {
        if (authUser?.section === "Санхүүгийн хэлтэс") {
          return "Тээврийн газар";
        } else if (
          authUser?.role === "Ахлах гаалийн бүртгэгдсэн мэргэжилтэн" ||
          authUser?.role === "Гаалийн бүртгэгдсэн мэргэжилтэн"
        ) {
          return "Тээврийн газар";
        } else {
          return authUser?.department || "null";
        }
      }

      if (authUser?.type === "Терминалын газрын хэрэглэгч") {
        return "Терминалын газар";
      }

      if (authUser?.type === "Тээврийн газрын хэрэглэгч") {
        return "Тээврийн газар";
      }

      return "null";
    };

    const fetchNotifications = () => {
      const tunnel = getTunnel();

      dispatch(
        onGetNotifications({
          ntf_tunnel: tunnel,
        })
      );
    };

    fetchNotifications();

    const interval = setInterval(fetchNotifications, 5000);
    return () => clearInterval(interval);
  }, [dispatch, authUser?.company, authUser?.department, authUser?.type]);

  useEffect(() => {
    const handleUserInteraction = () => setSoundAllowed(true);
    document.addEventListener("click", handleUserInteraction, { once: true });
    return () => document.removeEventListener("click", handleUserInteraction);
  }, []);

  useEffect(() => {
    if (Array.isArray(notifications) && Array.isArray(crmusers) && authUser) {
      const enrichedNotifications = notifications
        .filter((ntf) => ntf.sender_id !== authUser._id)
        .map((ntf) => {
          const sender = crmusers.find((u) => u._id === ntf.sender_id);

          let senderName = sender?.company || "N/A";
          let senderImage = companyImg;

          if (
            authUser.role === "Админ" ||
            authUser.role === "Терминалын газрын захирал" ||
            authUser.role === "Товарын хэлтсийн дарга" ||
            authUser.role === "Кассир" ||
            authUser.role === "Кассир" ||
            authUser.department === "Тээврийн газар" ||
            authUser.section === "Санхүүгийн хэлтэс"
          ) {
            if (sender?.company !== "TILogistic") {
              senderName = sender ? `${sender.company}` : "N/A";
              senderImage = sender?.image_url || dummyImg;
            } else {
              senderName = sender
                ? `${sender.last_name?.charAt(0).toUpperCase() || ""}.${
                    sender.first_name
                  } | ${sender.company}`
                : "N/A";

              senderImage = sender?.image_url || dummyImg;
            }
          }

          return {
            ...ntf,
            sender_image: senderImage,
            sender_name: senderName,
          };
        });

      const unreadCount = enrichedNotifications.filter((ntf) =>
        ntf.recipients.some(
          (r) => r.userId.toString() === authUser._id.toString() && !r.isRead
        )
      ).length;

      if (unreadCount > prevUnreadCountRef.current) {
        toast.info(
          `Шинэ мэдэгдэл ирлээ! (${unreadCount - prevUnreadCountRef.current})`,
          {
            autoClose: 5000,
            closeButton: ({ closeToast }) => (
              <button
                onClick={closeToast}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#1077d8ff",
                  fontSize: "16px",
                  cursor: "pointer",
                }}
              >
                ✖
              </button>
            ),
          }
        );
      }

      if (unreadCount > prevUnreadCountRef.current && soundAllowed) {
        const audio = new Audio("/sounds/notification.mp3");
        audio.play().catch((err) => console.log("Sound play error:", err));
      }

      prevUnreadCountRef.current = unreadCount;
      setUnreadNotificationsCount(unreadCount);
      setNotificationsList(enrichedNotifications);
    }
  }, [notifications, crmusers, soundAllowed]);

  const formatNotificationDate = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);

    const diffMs = now - date;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);

    if (diffHours < 12) {
      if (diffHours > 0) {
        return `${diffHours} цагийн өмнө`;
      } else if (diffMinutes > 0) {
        return `${diffMinutes} минутын өмнө`;
      } else {
        return `${diffSeconds} секундын өмнө`;
      }
    }

    // YYYY-MM-DD, HH:mm форматаар буцаах
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}, ${hours}:${minutes}`;
  };

  const handleDropdownOpen = () => {
    setSoundAllowed(true);

    const unreadIds = notificationsList
      ?.filter((ntf) =>
        ntf.recipients.some(
          (r) => r.userId === authUser._id && r.isRead === false
        )
      )
      .map((ntf) => ntf._id);

    if (unreadIds?.length > 0) {
      setTimeout(() => {
        dispatch(onGetUpdateNotification({ ids: unreadIds }));
      }, 1000);
    }
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  return (
    <React.Fragment>
      <Dropdown
        isOpen={isNotificationDropdown}
        toggle={toggleNotificationDropdown}
        className="topbar-head-dropdown ms-1 header-item"
      >
        <DropdownToggle
          type="button"
          tag="button"
          className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
          onClick={handleDropdownOpen}
        >
          <i className="bx bx-bell fs-22"></i>
          <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-info">
            {unreadNotificationsCount || 0}
            <span className="visually-hidden">уншаагүй мэдэгдэл</span>
          </span>
        </DropdownToggle>
        <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0">
          <div className="dropdown-head bg-primary bg-pattern rounded-top">
            <div className="p-3">
              <Row className="align-items-center">
                <Col>
                  <h6 className="m-0 fs-16 fw-semibold text-white">
                    {" "}
                    Мэдэгдэл{" "}
                  </h6>
                </Col>
                <div className="col-auto dropdown-tabs">
                  <span className="badge bg-light-subtle text-body fs-13">
                    {" "}
                    Уншаагүй: {unreadNotificationsCount || 0}
                  </span>
                </div>
              </Row>
            </div>

            <div className="px-2 pt-2">
              <Nav className="nav-tabs dropdown-tabs nav-tabs-custom">
                <NavItem>
                  <NavLink
                    href="#"
                    className={classnames({ active: activeTab === "1" })}
                    onClick={() => {
                      toggleTab("1");
                    }}
                  >
                    Нийт
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    href="#"
                    className={classnames({ active: activeTab === "2" })}
                    onClick={() => {
                      toggleTab("2");
                    }}
                  >
                    Мэдэгдэл
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    href="#"
                    className={classnames({ active: activeTab === "3" })}
                    onClick={() => {
                      toggleTab("3");
                    }}
                  >
                    Анхааруулга
                  </NavLink>
                </NavItem>
              </Nav>
            </div>
          </div>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="1" className="py-2 ps-2">
              <SimpleBar style={{ maxHeight: "300px" }} className="pe-2">
                {notificationsList && notificationsList.length > 0 ? (
                  notificationsList.map((ntf) => (
                    <div
                      key={ntf._id}
                      className="text-reset notification-item d-block dropdown-item position-relative"
                    >
                      <div className="d-flex">
                        <img
                          src={ntf?.sender_image || dummyImg}
                          className="me-3 rounded-circle avatar-xs"
                          alt="user-pic"
                        />
                        <div className="flex-grow-1">
                          <h6 className="mt-0 mb-1 fs-13 fw-semibold">
                            {ntf?.sender_name || "Unknown"}
                          </h6>
                          <div className="fs-13 text-muted">
                            <p
                              className={`mb-1 ${
                                ntf.recipients.some(
                                  (r) =>
                                    r.userId.toString() ===
                                      authUser._id.toString() && !r.isRead
                                )
                                  ? "text-info"
                                  : "text-muted"
                              }`}
                            >
                              {ntf.ntf_body}
                            </p>
                          </div>

                          <p className="mb-0 fs-11 fw-medium text-muted">
                            <span>
                              <i className="mdi mdi-clock-outline"></i>{" "}
                              {formatNotificationDate(ntf.ntf_date)}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <>
                    <div className="w-25 w-sm-50 pt-3 mx-auto">
                      <img
                        src={bell}
                        className="img-fluid"
                        alt="no-notification"
                      />
                    </div>
                    <div className="text-center pb-5 mt-2">
                      <h6 className="fs-18 fw-semibold lh-base">
                        Танд ирсэн мэдэгдэл байхгүй байна
                      </h6>
                    </div>
                  </>
                )}
              </SimpleBar>
            </TabPane>

            <TabPane tabId="2" className="py-2 ps-2">
              <SimpleBar style={{ maxHeight: "300px" }} className="pe-2">
                {notificationsList && notificationsList.length > 0 ? (
                  notificationsList.map((ntf) => (
                    <div
                      key={ntf._id}
                      className="text-reset notification-item d-block dropdown-item position-relative"
                    >
                      <div className="d-flex">
                        <img
                          src={ntf?.sender_image || dummyImg}
                          className="me-3 rounded-circle avatar-xs"
                          alt="user-pic"
                        />
                        <div className="flex-grow-1">
                          <h6 className="mt-0 mb-1 fs-13 fw-semibold">
                            {ntf?.sender_name || "Unknown"}
                          </h6>
                          <div className="fs-13 text-muted">
                            <p
                              className={`mb-1 ${
                                ntf.recipients.some(
                                  (r) =>
                                    r.userId.toString() ===
                                      authUser._id.toString() && !r.isRead
                                )
                                  ? "text-info"
                                  : "text-muted"
                              }`}
                            >
                              {ntf.ntf_body}
                            </p>
                          </div>

                          <p className="mb-0 fs-11 fw-medium text-muted">
                            <span>
                              <i className="mdi mdi-clock-outline"></i>{" "}
                              {formatNotificationDate(ntf.ntf_date)}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <>
                    <div className="w-25 w-sm-50 pt-3 mx-auto">
                      <img
                        src={bell}
                        className="img-fluid"
                        alt="no-notification"
                      />
                    </div>
                    <div className="text-center pb-5 mt-2">
                      <h6 className="fs-18 fw-semibold lh-base">
                        Танд ирсэн мэдэгдэл байхгүй байна
                      </h6>
                    </div>
                  </>
                )}
              </SimpleBar>
            </TabPane>
            <TabPane tabId="3" className="p-4">
              <div className="w-25 w-sm-50 pt-3 mx-auto">
                <img src={bell} className="img-fluid" alt="user-pic" />
              </div>
              <div className="text-center pb-5 mt-2">
                <h6 className="fs-18 fw-semibold lh-base">
                  Танд ирсэн анхааруулга байхгүй байна{" "}
                </h6>
              </div>
            </TabPane>
          </TabContent>
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

export default NotificationDropdown;
